// Orders helpers in "@jasper/shared" are typed with a custom "Order" type not fully compatible with codegen ones.
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useRef, useEffect, useState } from "react";
import { Table, MantineStyleProp, Button, Text, Group } from "@mantine/core";
import {
  isUserProvider,
  isUserStaff,
  isImplant,
  isOrderWithNoAttachement,
  isPAC,
  User,
  getDentistCode,
} from "@jasper/shared";
import { ORDER_NOTES } from "../../../constants/orders";
import { canSelectOrder } from "../../../utils/orderUtils";
import { OrderItem } from "../types";
import { IntegrationType, OrderStatus } from "../../../generated/graphql";
import { SelectableOrder } from "../hooks/useOrderSelection";

interface OrderListTableProps {
  data: OrderItem[];
  navigate: (url: string) => void;
  user: User;
  totalCount: number;
  toggleOrder: (order: SelectableOrder) => void;
  toggleSelectPage: (page: number, orders: SelectableOrder[]) => void;
  unselectAll: () => void;
  selectAllPages: () => void;
  selectedOrders: SelectableOrder[];
  selectedPages: number[];
  currentPage: number;
}

export const getBackgroundColor = (order: OrderItem): MantineStyleProp => {
  if (isPAC(order as any)) {
    return { backgroundColor: "blue", color: "white" };
  }
  if ((order?.redo ?? []).length > 0) {
    return { backgroundColor: "purple", color: "white" };
  }
  if (isImplant(order as any) && (order.hasComment ?? Boolean(order.comment))) {
    return { backgroundColor: "red", color: "white" };
  }
  if (isImplant(order as any)) {
    return { backgroundColor: "darkred", color: "white" };
  }
  if ((order?.orderComment ?? []).length > 0) {
    return { backgroundColor: "lightcoral", color: "white" };
  }
  if (order.status === OrderStatus.NeedToBeProcessed) {
    return { backgroundColor: "green", color: "white" };
  }
  return {};
};

const displayLastUpdate = (date: Date): string => {
  const diffMs = date.getTime() - new Date().getTime();
  const lastUpdateInDays = Math.round(Math.abs(diffMs / (24 * 60 * 60 * 1000)));

  return lastUpdateInDays >= 1
    ? date.toDateString()
    : date.toLocaleTimeString();
};

const OrderListTable = ({
  data,
  navigate,
  user,
  totalCount,
  toggleOrder,
  toggleSelectPage,
  selectAllPages,
  selectedOrders,
  selectedPages,
  currentPage,
  unselectAll,
}: OrderListTableProps) => {
  const checkAllCheckbox = useRef<HTMLInputElement>(null);
  const [areOrdersFromOtherPagesSelected, setAreOrdersFromOtherPagesSelected] =
    useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set<string>());

  useEffect(() => {
    setSelectedOrderIds(new Set(selectedOrders.map(({ id }) => id)));
  }, [selectedOrders]);

  // Update checkbox according to if page is selected or not
  useEffect(() => {
    if (checkAllCheckbox.current) {
      checkAllCheckbox.current.checked = selectedPages.includes(currentPage);
    }
  }, [selectedPages, currentPage]);

  useEffect(() => {
    setAreOrdersFromOtherPagesSelected(
      selectedPages.some(selectedPage => selectedPage !== currentPage) ||
        selectedOrders.some(selectedOrder =>
          data.every(({ id }) => id !== selectedOrder.id)
        )
    );
  }, [selectedPages, selectedOrders, data, currentPage]);

  const handleOrderClick = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const renderOrderNotes = (order: OrderItem): string => {
    const notes: string[] = [];

    if (order?.isRush) notes.push(ORDER_NOTES.RUSH);
    if (order?.isPhysicalPrint) notes.push(ORDER_NOTES.PHYSICAL_PRINT);
    if ((order?.orderComment ?? []).length > 0) notes.push(ORDER_NOTES.COMMENT);
    if (isImplant(order as any)) notes.push(ORDER_NOTES.IMPLANT);
    if (isOrderWithNoAttachement(order as any))
      notes.push(ORDER_NOTES.NO_ATTACHMENT);
    if (isPAC(order as any)) notes.push(ORDER_NOTES.PAC);
    if ((order?.redo ?? []).length > 0) notes.push(ORDER_NOTES.REDO);
    if (order.status === OrderStatus.NeedToBeProcessed) {
      if (order.integrationType === IntegrationType.Threeshape)
        notes.push(ORDER_NOTES.COMMUNICATE);
      if (order.integrationType === IntegrationType.Meditlink)
        notes.push(ORDER_NOTES.MEDITLINK);
    }

    return notes.join(" ");
  };

  const renderDoctorName = (order: OrderItem): string => {
    if (isUserProvider(user)) {
      return getDentistCode(order?.user);
    }
    return `Dr ${order?.user?.firstName} ${order?.user?.lastName}`;
  };

  const renderProductType = (order: OrderItem): string => {
    const products = order.products ?? [];
    if (products.length > 1) return "Multiple";
    return products[0]?.productType ?? "-";
  };

  return (
    <>
      {(selectedOrders.length > 1 || areOrdersFromOtherPagesSelected) && (
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: "#e7f5ff",
            border: "1px solid #339af0",
            borderRadius: "4px",
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Text size="sm">
            {areOrdersFromOtherPagesSelected
              ? `${selectedOrders.length} orders are selected on ${selectedOrders.length === 1 ? "a different page" : "multiple pages"}.`
              : `${selectedOrders.length} orders on this page are selected.`}
          </Text>
          <Group>
            {selectedOrders.length < totalCount && (
              <Button size="xs" variant="light" onClick={selectAllPages}>
                Select all {totalCount} orders
              </Button>
            )}
            <Button size="xs" variant="light" onClick={unselectAll}>
              Unselect all
            </Button>
          </Group>
        </div>
      )}
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <label className="label-checkbox" htmlFor="checkAll">
                <input
                  id="checkAll"
                  ref={checkAllCheckbox}
                  type="checkbox"
                  onChange={() => toggleSelectPage(currentPage, data)}
                  aria-label="Select all orders"
                />
              </label>
            </Table.Th>
            <Table.Th>Note</Table.Th>
            <Table.Th>Reference</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Doctor</Table.Th>
            <Table.Th>Patient</Table.Th>
            <Table.Th>Product</Table.Th>
            {isUserStaff(user) && (
              <>
                <Table.Th>Delivery Date</Table.Th>
                <Table.Th>Last update</Table.Th>
              </>
            )}
            {isUserProvider(user) && <Table.Th>Last shipping date</Table.Th>}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(data ?? []).map((order: OrderItem) => (
            <Table.Tr
              style={{ cursor: "pointer" }}
              key={order.id}
              onClick={() => handleOrderClick(order.id)}
              role="row"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleOrderClick(order.id);
                }
              }}
            >
              <Table.Td onClick={e => e.stopPropagation()}>
                {canSelectOrder(order as any) && (
                  <label
                    className="label-checkbox"
                    htmlFor={`check-${order.id}`}
                  >
                    <input
                      id={`check-${order.id}`}
                      type="checkbox"
                      value={order.id}
                      name="selectOrder"
                      checked={selectedOrderIds.has(order.id)}
                      onChange={() => toggleOrder(order)}
                      aria-label={`Select order ${order.orderReference}`}
                    />
                  </label>
                )}
              </Table.Td>
              <Table.Td style={getBackgroundColor(order)}>
                {renderOrderNotes(order)}
              </Table.Td>
              <Table.Td>{order.orderReference}</Table.Td>
              <Table.Td>{order.status}</Table.Td>
              <Table.Td>{renderDoctorName(order)}</Table.Td>
              <Table.Td>
                {order?.patient?.firstName} {order?.patient?.lastName}
              </Table.Td>
              <Table.Td>{renderProductType(order)}</Table.Td>
              {isUserStaff(user) && (
                <>
                  <Table.Td>
                    {order.deliveryDate
                      ? new Date(order.deliveryDate).toDateString()
                      : "-"}
                  </Table.Td>
                  <Table.Td>
                    {displayLastUpdate(
                      new Date(order.updatedAt ?? order.createdAt)
                    )}
                  </Table.Td>
                </>
              )}
              {isUserProvider(user) && (
                <Table.Td>
                  {order?.lastShippingDate
                    ? new Date(order?.lastShippingDate).toDateString()
                    : "-"}
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </>
  );
};

export default OrderListTable;
